import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useState, createContext, useContext } from 'react';
import { themed } from '../theme';
import { Box } from './Box';
import { Icon } from './Icon';

// Tworzenie kontekstu dla Dropdown
const DropdownContext = createContext({});

// Komponent DropdownInnerDOM z useState
const DropdownInnerDOM = ({ children, onChange, ...rest }) => {
  const [open, setOpen] = useState(false);

  const onChangeAndClose = (x) => {
    setOpen(false); // Zamknij dropdown po wybraniu wartości
    if (onChange) onChange(x);
  };

  return (
      <DropdownContext.Provider value={{ open, onChange: onChangeAndClose }}>
        <Box
            {...rest}
            onClick={() => setOpen((prev) => !prev)} // Toggle stanu
            onBlur={() => setOpen(false)} // Zamknij przy utracie fokusu
            tabIndex={-1}
        >
          {children}
        </Box>
      </DropdownContext.Provider>
  );
};

// Definicja Dropdown
export const Dropdown = themed({
  tag: DropdownInnerDOM,
  defaultTheme: {
    dropdown: {
      display: 'flex',
      borderRadius: 'medium',
      border: 'regular',
      borderColor: 'secondaryDark',
      css: ({ theme }) => ({
        userSelect: 'none',
        position: 'relative',
        ':hover': {
          borderColor: theme.colors.primary,
        },
        ':focus': {
          outline: 'none',
        },
      }),
    },
  },
});

// Komponent DropdownLabelInnerDOM z useContext
const DropdownLabelInnerDOM = ({ children, ...rest }) => {
  const { open } = useContext(DropdownContext);

  return (
      <Box {...rest}>
        <Box flex="1">{children}</Box>
        <Icon src={open ? 'dropdownArrowUp' : 'dropdownArrowDown'} fallback={open ? '▴' : '▾'} />
      </Box>
  );
};

// Definicja DropdownLabel
export const DropdownLabel = themed({
  tag: DropdownLabelInnerDOM,
  defaultTheme: {
    dropdownLabel: {
      display: 'flex',
      py: 'xs',
      px: 'sm',
      fontSize: 'sm',
      justifyContent: 'space-between',
      css: {
        width: '100%',
        cursor: 'pointer',
      },
    },
  },
});

// Komponent DropdownMenuInnerDOM z useContext
const DropdownMenuInnerDOM = (props) => {
  const { open } = useContext(DropdownContext);
  return open ? <Box as="ul" {...props} display={open ? 'block' : 'none'} /> : null;
};

// Definicja DropdownMenu
export const DropdownMenu = themed({
  tag: DropdownMenuInnerDOM,
  defaultTheme: {
    dropdownMenu: {
      m: 'none',
      p: 'none',
      borderRadius: 'medium',
      boxShadow: 'subtle',
      bg: 'white',
      css: ({ theme }) => ({
        position: 'absolute',
        listStyle: 'none',
        top: 'calc(100% + 1px)',
        left: 0,
        right: 0,
        zIndex: theme.zIndex.dropDownMenu,
      }),
      variants: {
        above: {
          css: {
            top: 'auto',
            bottom: 'calc(100% + 1px)',
          },
        },
      },
    },
  },
});

// Komponent DropdownMenuItemInnerDOM z useContext
const DropdownMenuItemInnerDOM = (props) => {
  const { onChange } = useContext(DropdownContext);
  return (
      <Box
          as="li"
          {...props}
          onClick={() => onChange && onChange(props.value)}
      />
  );
};

// Definicja DropdownMenuItem
export const DropdownMenuItem = themed({
  tag: DropdownMenuItemInnerDOM,
  defaultProps: {
    value: undefined,
  },
  defaultTheme: {
    dropdownMenuItem: {
      p: 'xs',
      css: ({ theme }) => ({
        cursor: 'pointer',
        ':hover': {
          background: theme.colors.primaryLight,
        },
      }),
    },
  },
});